import React, {
  useReducer,
  useEffect,
  createContext,
  useContext,
  useRef,
  PropsWithChildren,
} from 'react'
import { isIOS, isTablet } from 'react-device-detect'
import im from 'immer'
import QueryString from 'qs'

import { Platform, desktopPlatforms, mobilePlatforms } from '@alucio/core'
import { CrmIntegrationType } from '@alucio/aws-beacon-amplify/src/models'
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal'
import DNAVeevaVaultLogin from 'src/components/DNA/Modal/DNAVeevaVaultLogin'
import workerChannel from 'src/worker/channels/workerChannel'
import config from 'src/config/app.json'
import { useDispatch } from 'src/state/redux'
import { useUserTenant } from '../redux/selector/user'
import { usePermissions } from '../redux/selector/permissions'
import { cacheActions } from '../redux/slice/Cache/cache'
import { drawerActions, DRAWER_ENTITIES } from '../redux/slice/drawer'
import { useInterpret, useSelector } from '@xstate/react'
import useMonitorConnection from './useMonitorConnection'
import { reloadApp } from 'src/utils'

/* CRM MACHINE */
import CRMMachine from 'src/state/machines/CRM/crmMachine'
import {
  CRM_AUTH_INFORMATION,
  CRM_STORAGE_UPDATE,
  CRMIntegrationSession,
  CRM_CODE,
  CRMUtil,
} from '../machines/CRM/util'
import { CRMConnectionStatus, CRMService, CRMSyncStatus } from '../machines/CRM/crmMachineTypes'
import {
  clearCRMAutomaticLoginAttempts,
  ensureCRMAutomaticLoginPreference,
  hasReachedCRMAutomaticLoginAttemptLimit,
  recordCRMAutomaticLoginAttempt,
  shouldAttemptInitialCRMAutomaticLogin,
} from '../machines/CRM/automaticLogin'
/* LOGOUT MACHINE */
import { logOutMachine, LogOutService } from 'src/state/machines/auth/logOutMachine'
/* VALIDATE USER MACHINE */
import { validateUserMachine, ValidateUserService } from '../machines/auth/validateUserMachine'
/* LOGGER MACHINE */
import LoggerStateProvider from '../machines/logger/LoggerProvider'
import * as logger from 'src/utils/logger'
/* ANALYTICS MACHINE */
// @ts-ignore -- this is injected at build time
// and we have backup optional chaining usage on it
const ENV_NAME = config.ENV_NAME as string
const crmUtil = new CRMUtil();
const originalSearchParams = window.location.search
const LOCALSTORAGE_DEBUG_KEY = 'debugMachines';
const COMPACT_LAYOUT_BREAKPOINT = 900;
const isHandlingCRMAuthRedirect = () => {
  return new URLSearchParams(window.location.search).has('code');
}

export enum DebuggableMachines {
  AnalyticsSM = 'AnalyticsSM',
  PublisherVersioningSM = 'PublisherVersioningSM',
}

export enum DeviceMode {
  'desktop' = 'desktop',
  'tablet' = 'tablet',
}

type AppSettingsState = {
  // [TODO] - Remove this, responsiveness should not be handle by a global breakpoint at the top level
  //        - This encourages bad responsive practices due to an over simplification
  isCompactLayoutDeprecate: boolean
  deviceMode: DeviceMode | keyof typeof DeviceMode
  /** NOTE: This serves a similar purpose as deviceMode but allows for the additional distinction between desktop
   * and tablet PWA. Ideally, we'd use a single approach but we'll keep deviceMode for now to mitigate refactor risk.
   */
  platform: Platform
  isWebPlatform: boolean
  isTabletPlatform: boolean
  isDeviceSwitchEnabled: boolean
  isPWAStandalone: boolean
  isOfflineEnabled?: boolean
  isCRMEnabled?: boolean
  isPWAInstalled?: boolean
  isIdleDisabled: boolean
  isSSO: boolean
  isOnline: boolean
  initialUrlSearchParams: URLSearchParams
  crmIntegrationSession?: CRMIntegrationSession
  debugStateMachines: Record<keyof typeof DebuggableMachines | DebuggableMachines, boolean>
}

type AppSettingsAction =
  | { type: 'setisCompactLayoutDeprecate', payload: AppSettingsState['isCompactLayoutDeprecate'] }
  | { type: 'toggleDeviceMode' }
  | { type: 'setIsSSO' }
  | { type: 'setIsPWAStandalone', payload: AppSettingsState['isPWAStandalone'] }
  | { type: 'setIsOfflineEnabled', payload: AppSettingsState['isOfflineEnabled'] }
  | { type: 'setIsPWAInstalled', payload: AppSettingsState['isPWAInstalled'] }
  | { type: 'setIsOnline', payload: AppSettingsContextValue['isOnline'] }
  | { type: 'setCRMAuthInformation', payload: AppSettingsState['crmIntegrationSession'] }
  | { type: 'setIsCRMEnabled', payload: AppSettingsState['isCRMEnabled'] }
  | { type: 'toggleDebugStateMachine', payload: keyof typeof DebuggableMachines | DebuggableMachines }

type AppSettingsContextValue = AppSettingsState & {
  dispatch: React.Dispatch<AppSettingsAction>
  crmMachine: React.MutableRefObject<CRMService>['current']
  logOutMachine: React.MutableRefObject<LogOutService>['current']
  isLoggingOut: boolean
  validateLogInUserMachine: React.MutableRefObject<ValidateUserService>['current']
}

export const getDebugMachinesFromStorage = () => {
  try {
    const storedValue = localStorage.getItem(LOCALSTORAGE_DEBUG_KEY);
    const parsed = storedValue ? JSON.parse(storedValue) : {}
    if (!Object.keys(parsed).every(key => DebuggableMachines[key] && typeof parsed[key] === 'boolean')) {
      throw new Error('Found unsupported machine configuration', parsed);
    }
    return parsed as AppSettingsState['debugStateMachines'];
  } catch (error) {
    console.error('Error reading debug state from localStorage:', error);
    return {} as AppSettingsState['debugStateMachines'];
  }
};

export const setDebugMachinesToStorage = (debugMachines: Record<string, boolean>) => {
  try {
    localStorage.setItem(LOCALSTORAGE_DEBUG_KEY, JSON.stringify(debugMachines));
  } catch (error) {
    console.error('Error saving debug state to localStorage:', error);
  }
};

const AppSettingsContext = createContext<AppSettingsContextValue>(null!)
AppSettingsContext.displayName = 'AppSettingsContext'
export const useAppSettings = () => useContext(AppSettingsContext)

const appSettingsReducer = (state: AppSettingsState, action: AppSettingsAction) => {
  switch (action.type) {
    case 'setisCompactLayoutDeprecate': {
      return im(state, draft => {
        draft.isCompactLayoutDeprecate = action.payload
      })
    }
    case 'toggleDeviceMode': {
      return im(state, draft => {
        draft.deviceMode = state.deviceMode === 'desktop'
          ? 'tablet'
          : 'desktop'
        logger.appInit.appSettings.info('Device mode toggled', draft.deviceMode)
      })
    }
    case 'setIsSSO': {
      return im(state, draft => {
        draft.isSSO = true
        logger.appInit.appSettings.info('SSO set to true')
      })
    }
    case 'setIsOfflineEnabled': {
      return im(state, draft => {
        draft.isOfflineEnabled = !!action.payload
        logger.appInit.appSettings.info('Offline enabled set to', draft.isOfflineEnabled)
        if (action.payload &&
          !state.isPWAStandalone &&
          state.isPWAInstalled === undefined &&
          !isIOS) {
          // If we don't know for sure the PWA is not installed we assume it is until we
          // get confirmation (via onbeforeinstallprompt that it isn't)
          // This is only for non-iOS since safari doesn't support onbeforeinstallprompt
          logger.appInit.appSettings.debug('Disabling Idle')
          draft.isIdleDisabled = true
        }
      })
    }
    case 'setIsPWAStandalone': {
      return im(state, draft => {
        // If we are in PWA mode we ignore changing the value.
        // This means that if a user goes from PWA mode back to the browser, they will remain in PWA
        // This was a workaround in the case the user goes to PWA and goes to fullscreen ( in the previous implmentation the user would switch to deskptop mode from pwa)
        if (!state.isPWAStandalone) {
          draft.isPWAStandalone = !!action.payload
          draft.deviceMode = action.payload ? 'tablet' : 'desktop'
          logger.appInit.appSettings.info('PWA standalone set to', draft.isPWAStandalone)
        }
      })
    }
    case 'setIsPWAInstalled': {
      return im(state, draft => {
        draft.isPWAInstalled = !!action.payload
        logger.appInit.appSettings.info('PWA installed set to', draft.isPWAInstalled)
      })
    }
    case 'setIsOnline': {
      return im(state, draft => {
        draft.isOnline = action.payload
        logger.appInit.appSettings.info('Online status set to', draft.isOnline)
      })
    }
    case 'setCRMAuthInformation': {
      return im(state, draft => {
        draft.crmIntegrationSession = action.payload
      })
    }
    case 'setIsCRMEnabled': {
      return im(state, draft => {
        draft.isCRMEnabled = action.payload
        logger.appInit.appSettings.info('CRM enabled set to', draft.isCRMEnabled)
      })
    }
    case 'toggleDebugStateMachine': {
      return im(state, draft => {
        const machineId = action.payload;
        const updated = {
          ...draft.debugStateMachines,
          [machineId]: !draft.debugStateMachines[machineId],
        };
        setDebugMachinesToStorage(updated);
        draft.debugStateMachines = updated;
        logger.appInit.appSettings.info(
          `State machine ${machineId} debug mode is set to`, !draft.debugStateMachines[machineId],
        )
        window.location.reload();
      })
    }
    default: return state
  }
}

export const AppSettings: React.FC<PropsWithChildren> = (props) => {
  const { children } = props
  const userTenant = useUserTenant()
  const permissions = usePermissions()
  const isPWAStandalone = window.matchMedia('(display-mode: standalone)').matches
  const isTabletIOS = isTablet && isIOS;
  const queryParams = QueryString.parse(window.location.search, { ignoreQueryPrefix: true })
  const reduxDispatch = useDispatch()
  /* eslint-disable-next-line */
  const enableDeviceSwitch = (
    ((process.env.NODE_ENV === 'development' ||
      ENV_NAME?.includes('develop')) && isPWAStandalone) ||
    queryParams?.enableDeviceSwitch === 'true'
  )
  const isTabletQueryString = queryParams?.tabletMode
  const initialUrlSearchParams = new URLSearchParams(originalSearchParams);
  const mockOffline = queryParams?.mockOffline === 'true';
  const isCRMEnabled = !!userTenant?.config.crmIntegration

  /** This assumes we only have the two devicemodes desktop/tablet but
   * should be reworked if we ever add detection for additional devices.
   * Additionally, isDesktop appears to be returning true even on the iOS
   * simulator 🤷🏽‍♂️ */
  const currentPlatform: AppSettingsState['platform'] = isPWAStandalone
    ? isIOS
      ? 'tabletPWA'
      : 'desktopPWA'
    : isIOS
      ? 'tabletWEB'
      : 'desktopWEB'

  const deviceMode = (isTabletIOS || isPWAStandalone || isTabletQueryString)
    ? DeviceMode.tablet
    : DeviceMode.desktop

  // Calculate initial responsive values - use window.innerWidth directly to avoid hook re-renders
  const initialIsCompactLayout = typeof window !== 'undefined' ? window.innerWidth <= COMPACT_LAYOUT_BREAKPOINT : false

  const [
    appSettings,
    dispatch,
  ] = useReducer(
    appSettingsReducer,
    {
      isCompactLayoutDeprecate: initialIsCompactLayout,
      deviceMode,
      platform: currentPlatform,
      isWebPlatform: desktopPlatforms.includes(currentPlatform),
      isTabletPlatform: mobilePlatforms.includes(currentPlatform),
      isDeviceSwitchEnabled: enableDeviceSwitch,
      isOfflineEnabled: undefined,
      isSSO: false,
      isPWAStandalone: isPWAStandalone,
      isIdleDisabled: isPWAStandalone,
      isOnline: mockOffline ? false : navigator.onLine,
      initialUrlSearchParams,
      crmIntegrationSession: crmUtil.loadAuthInformation(),
      isCRMEnabled: isCRMEnabled,
      debugStateMachines: getDebugMachinesFromStorage(),
    },
  )
  const isOnline = useMonitorConnection(appSettings.isOfflineEnabled);
  const onlineStatus = mockOffline ? false : isOnline;

  /* CRM STATE MACHINE START */
  // [TODO-BEAC-6355] - Upgrade to latest state machine patterns
  const CRMService = useRef(
    useInterpret(
      () => CRMMachine(),
    ),
  )

  // [TODO-BEAC-6355] - This should not be in the render statement, please move to useEffect, or replace above with `useLazyRef` and set the transition there
  CRMService.current.onTransition((_state) => {
    // Commented for now to prevent logging of state
    // which includes all CRM accounts (might be causing OOM with lots of accts)
    // logger.appInit.appSettings.debug('CRM Machine Transition', state);
  })
  /* CRM STATE MACHINE END */

  /* LOGOUT STATE MACHINE START */
  // [TODO-BEAC-6355]
  const signOutMachine = useRef(
    useInterpret(
      () => logOutMachine({
        isOfflineEnabled: appSettings.isOfflineEnabled,
        isSSO: appSettings.isSSO,
      }),
    ),
  )

  // [TODO-BEAC-6355] - The selector should be memoized
  const isLoggingOut = useSelector(signOutMachine.current, (state) => state.tags.has('IS_LOGGING_OUT'));
  const isLoginState = useSelector(CRMService.current, (state) => state.matches('login'));
  const canStartCRMLogin = useSelector(CRMService.current, (state) => state.can('LOGIN_CRM'));
  const crmConnectionStatus = useSelector(CRMService.current, (state) => state.context.connectionStatus);
  const isCRMBusy = useSelector(CRMService.current, (state) => (
    state.context.isAuthenticating ||
    [CRMSyncStatus.SYNCING, CRMSyncStatus.RE_SYNCING].includes(state.context.crmSyncStatus) ||
    state.hasTag('REFRESH_DATA') ||
    state.hasTag('REFRESH_TOKEN')
  ));
  const didAttemptAutomaticCRMLogin = useRef(false);
  const automaticLoginScopeRef = useRef<string | undefined>(undefined);
  /* LOGOUT STATE MACHINE END */

  /* VALIDATE USER STATE MACHINE START */
  // [TODO-BEAC-6355]
  const validateLogInUserMachine = useRef(
    useInterpret(
      () => validateUserMachine,
    ),
  )
  const isValidatingUser = useSelector(validateLogInUserMachine.current, (state) => state.matches('validateUser'));
  /* VALIDATE USER STATE MACHINE END */

  // [TODO] - Break this into two discrete operations for better readability
  useEffect(() => {
    let isRefreshing = false;
    let updateInterval = 0;

    // Adds an Interval and a Listener to handle SW updates
    const handleServiceWorkerUpdates = (registration: ServiceWorkerRegistration) => {
      logger.offline.serviceWorker.debug('Detected ServiceWorker. Adding listener for new versions');
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        logger.offline.serviceWorker.debug('serviceworker change detected')
        if (!isRefreshing) {
          reloadApp()
          isRefreshing = true
        }
      });
      // Every 30 mins we check to see if there's a new version
      updateInterval = window.setInterval(() => {
        logger.offline.serviceWorker.debug('Checking for SW Updates')
        registration.update()
      }, 30 * 60 * 1000)
    };

    // Update service worker status
    const checkIsServiceWorkerRegistered = async () => {
      const registration = await navigator.serviceWorker.getRegistration('/')
      if (registration) {
        handleServiceWorkerUpdates(registration);
      }
      dispatch({
        type: 'setIsOfflineEnabled',
        payload: !!registration,
      })
      logger.offline.serviceWorker.info('Service worker registration status checked')
    }
    // Service worker is always registered on iOS or when running standalone
    if (navigator.serviceWorker) {
      if (isPWAStandalone || isIOS) {
        logger.offline.serviceWorker.debug('registering service worker')
        navigator.serviceWorker
          .register('/worker.js')
          .then(registration => {
            logger.offline.serviceWorker.info('SW registered: ', registration);
            handleServiceWorkerUpdates(registration);
            dispatch({
              type: 'setIsOfflineEnabled',
              payload: true,
            })
          })
          .catch(error => {
            logger.offline.serviceWorker.error('SW Registration failed:', error)
            throw Error('SW registration failed: ' + error)
          });
      } else {
        checkIsServiceWorkerRegistered()
      }
    }
    const beforeInstallListener = (e) => {
      logger.appInit.appSettings.debug('beforeInstall Event Triggered', e)
      dispatch({
        type: 'setIsPWAInstalled',
        payload: false,
      })
    }
    window.addEventListener('beforeinstallprompt', beforeInstallListener);
    logger.appInit.appSettings.debug('adding event listener for display-mode')
    // Watch for changes in install mode (chrome install does not reload the page)
    // Safari only supports addListener even though its deprecated
    window.matchMedia('(display-mode: standalone)').addListener((evt) => {
      logger.appInit.appSettings.debug('Display mode event listener fired', evt.matches)
      dispatch({
        type: 'setIsPWAStandalone',
        payload: evt.matches,
      })
    });
    return () => {
      if (updateInterval) { clearInterval(updateInterval) }
      window.removeEventListener('beforeinstallprompt', beforeInstallListener)
    }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      const newIsCompactLayout = window.innerWidth <= COMPACT_LAYOUT_BREAKPOINT;

      if (appSettings.isCompactLayoutDeprecate !== newIsCompactLayout) {
        dispatch({ type: 'setisCompactLayoutDeprecate', payload: newIsCompactLayout });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [appSettings.isCompactLayoutDeprecate]);

  useEffect(() => {
    // ಠ_ಠ
    if (isOnline !== undefined && appSettings.isOnline !== isOnline) {
      dispatch({ type: 'setIsOnline', payload: onlineStatus })
      reduxDispatch(cacheActions.setOnline(onlineStatus))
      // [TODO-PWA]
      //  - Move into separate useEffect?
      //  - We probably shouldn't rely on the client
      //    instead the worker should have its own listener
      const networkStatus = onlineStatus ? 'ONLINE' : 'OFFLINE'
      logger.PWALogger.info('Sending Network Signal -', networkStatus)
      workerChannel.postMessageExtended({
        type: 'NETWORK_STATUS',
        value: networkStatus,
      })
    }
  }, [isOnline])

  // We mirror a copy of the isOnline value to Redux for selector purposes
  useEffect(
    () => { reduxDispatch(cacheActions.setOnline(appSettings.isOnline)) },
    [appSettings.isOnline],
  )

  // Update the CRM connection status
  useEffect(() => {
    const consumePostAuthIntent = () => {
      const intent = CRMUtil.consumePostAuthIntent();
      if (intent?.type === 'MEETING_SUBMIT') {
        reduxDispatch(drawerActions.toggle({
          entity: DRAWER_ENTITIES.MEETING,
          entityId: intent.meetingId,
          keepOpen: true,
          autoSubmitToCRM: true,
          autoSubmitToCRMSubmitType: intent.submitType,
        }));
      }
      if (intent?.type === 'CRM_FORM_SUBMIT') {
        reduxDispatch(drawerActions.toggle({
          entity: DRAWER_ENTITIES.CRM_FORM,
          entityId: intent.recordId,
          standaloneForm: intent.standaloneForm,
          keepOpen: true,
          autoSubmitToCRM: true,
        }));
      }
    }

    const handleStorageChange = async (e) => {
      if (e.key === CRM_AUTH_INFORMATION || e.type === CRM_STORAGE_UPDATE) {
        const CRMSettings = crmUtil.loadAuthInformation()
        logger.appInit.appSettings.debug('[CRM_SYNC_DEBUG] AppSettings received CRM auth update', {
          eventKey: e.key,
          eventType: e.type,
          hasCRMSettings: Boolean(CRMSettings),
        })
        dispatch({
          type: 'setCRMAuthInformation',
          payload: CRMSettings,
        })

        const crmMachineState = CRMService.current.state
        const isMachineMissingSession = !crmMachineState.context.crmSession?.accessToken &&
          !crmMachineState.context.crmSession?.encryptedSessionId
        logger.appInit.appSettings.debug('[CRM_SYNC_DEBUG] CRM machine state on auth update', {
          hasConfig: Boolean(crmMachineState.context.crmConfig),
          isMachineMissingSession,
          machineState: crmMachineState.value,
          syncStatus: crmMachineState.context.crmSyncStatus,
        })
        if (
          CRMSettings &&
          crmMachineState.context.crmConfig &&
          isMachineMissingSession &&
          !crmMachineState.matches('login')
        ) {
          logger.appInit.appSettings.debug('[CRM_SYNC_DEBUG] Sending LOAD_SESSION_DATA from AppSettings')
          CRMService.current.send({
            type: 'LOAD_SESSION_DATA',
            payload: {
              crmSession: CRMSettings,
            },
          })
        } else {
          logger.appInit.appSettings.debug('[CRM_SYNC_DEBUG] Not sending LOAD_SESSION_DATA from AppSettings', {
            hasCRMSettings: Boolean(CRMSettings),
            hasConfig: Boolean(crmMachineState.context.crmConfig),
            isLoginState: crmMachineState.matches('login'),
            isMachineMissingSession,
          })
        }

        consumePostAuthIntent();
      }
      if (e.key === CRM_CODE) {
        const crmCode = localStorage.getItem(CRM_CODE)

        if (!crmCode) {
          logger.appInit.appSettings.warn('CRM code not found in local storage')
          return
        }

        const authInformation = await crmUtil.getCRMAuthInformation(crmCode)
        localStorage.setItem(CRM_AUTH_INFORMATION, JSON.stringify(authInformation))
        localStorage.removeItem(CRM_CODE)
        logger.appInit.appSettings.info('CRM code processed and auth information updated')
      }
    }

    // [TODO] - This is randomnly started in here, there's another useEffect where this can be more co-located
    // init the validate user machine
    validateLogInUserMachine.current.start()
    // Recovers existing iPad sessions that already have a stranded CRM auth code.
    crmUtil.recoverStrandedCRMAuthCode({ isOnline: appSettings.isOnline })
    if (crmUtil.loadAuthInformation()) {
      consumePostAuthIntent();
    }
    // this is need it in case the clean up is made from other tab
    window.addEventListener('storage', handleStorageChange);
    // this will be used in case the clean up is made from the same tab
    window.addEventListener(CRM_STORAGE_UPDATE, handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener(CRM_STORAGE_UPDATE, handleStorageChange)
    };
  }, []);

  useEffect(() => {
    dispatch({
      type: 'setIsCRMEnabled',
      payload: isCRMEnabled,
    })
    logger.appInit.appSettings.info('CRM enabled status set to', isCRMEnabled)
  }, [isCRMEnabled])

  useEffect(() => {
    const crmIntegration = userTenant?.config.crmIntegration;
    const automaticLoginScope = userTenant?.id
      ? [
        userTenant.id,
        crmIntegration?.name,
        crmIntegration?.crmIntegrationType,
        crmIntegration?.instanceUrl,
      ].join(':')
      : undefined;

    if (
      automaticLoginScopeRef.current &&
      automaticLoginScopeRef.current !== automaticLoginScope
    ) {
      clearCRMAutomaticLoginAttempts();
    }

    automaticLoginScopeRef.current = automaticLoginScope;
    didAttemptAutomaticCRMLogin.current = false;
  }, [userTenant?.id, userTenant?.config.crmIntegration])

  useEffect(() => {
    if (crmConnectionStatus === CRMConnectionStatus.CONNECTED) {
      clearCRMAutomaticLoginAttempts();
    }
  }, [crmConnectionStatus])

  useEffect(() => {
    if (userTenant?.config.crmIntegration && !isValidatingUser) {
      ensureCRMAutomaticLoginPreference();
    }
  }, [isValidatingUser, userTenant?.config.crmIntegration])

  useEffect(() => {
    const crmIntegration = userTenant?.config.crmIntegration;
    const isAutomaticLoginFeatureEnabled = permissions.BEAC_7921_crm_automatic_login;
    if (!crmIntegration ||
      didAttemptAutomaticCRMLogin.current ||
      isValidatingUser ||
      isHandlingCRMAuthRedirect()) {
      return;
    }

    const isUserPreferenceEnabled = isAutomaticLoginFeatureEnabled
      ? ensureCRMAutomaticLoginPreference()
      : false;

    if (crmConnectionStatus === CRMConnectionStatus.CONNECTED) {
      didAttemptAutomaticCRMLogin.current = true;
      return;
    }

    const shouldAttemptAutomaticLogin = shouldAttemptInitialCRMAutomaticLogin({
      connectionStatus: crmConnectionStatus,
      crmIntegration,
      hasReachedAttemptLimit: hasReachedCRMAutomaticLoginAttemptLimit(),
      isCRMBusy,
      isFeatureFlagEnabled: isAutomaticLoginFeatureEnabled,
      isOnline: appSettings.isOnline,
      isUserPreferenceEnabled,
    });

    if (!shouldAttemptAutomaticLogin || !canStartCRMLogin) {
      return;
    }

    didAttemptAutomaticCRMLogin.current = true;
    recordCRMAutomaticLoginAttempt();
    CRMService.current.send({
      type: 'LOGIN_CRM',
      payload: crmIntegration,
      options: {
        authMode: isTabletIOS ? 'redirect' : 'popup',
        trigger: 'APP_LOAD',
      },
    });
  }, [
    appSettings.isOnline,
    canStartCRMLogin,
    crmConnectionStatus,
    isCRMBusy,
    isValidatingUser,
    isTabletIOS,
    permissions.BEAC_7921_crm_automatic_login,
    userTenant?.config.crmIntegration,
  ])

  useEffect(() => {
    const isVeevaVaultIntegration =
      userTenant?.config.crmIntegration?.crmIntegrationType === CrmIntegrationType.VEEVA_VAULT;
    if (isLoginState && isVeevaVaultIntegration) {
      reduxDispatch(DNAModalActions.pushModal((modalProps) => (
        <DNAVeevaVaultLogin
          {...modalProps}
          closeModal={modalProps.toggleModal}
        />
      )));
    }
  }, [isLoginState]);

  return (
    <AppSettingsContext.Provider
      value={{
        ...appSettings,
        dispatch,
        crmMachine: CRMService.current,
        isLoggingOut,
        logOutMachine: signOutMachine.current,
        validateLogInUserMachine: validateLogInUserMachine.current,
      }}
    >
      <LoggerStateProvider
        deviceMode={deviceMode}
        currentPlatform={currentPlatform}
        isLoggingOut={isLoggingOut}
      >
        {children}
      </LoggerStateProvider>
    </AppSettingsContext.Provider>
  )
}

export default AppSettings
